import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { mainSiteUrl, categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.shoppingGuide"), href: "/usfans-buying-guide" },
    { label: t("nav.categories"), href: "/#categories" },
    { label: t("nav.guides"), href: "/#guides" },
    { label: t("nav.faq"), href: "/#faq" },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b bg-background-50 transition-all duration-300 ${
        scrolled ? "border-background-300" : "border-background-200/70"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 text-foreground-950">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-links-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight">{t("brand")}</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("nav.cta")}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-900 md:hidden cursor-pointer"
          aria-label="Menu"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setMenuOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* 移动端分类导航 */}
          <div className="mt-3 rounded-lg border border-background-200 bg-background-100 p-3">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-sm font-semibold text-foreground-900 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                  <i className="ri-grid-line"></i>
                </span>
                {t("nav.categories")}
              </span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={catOpen ? "ri-arrow-up-s-line text-foreground-500" : "ri-arrow-down-s-line text-foreground-500"}></i>
              </span>
            </button>

            {catOpen && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {categories.map((c) => (
                  <a
                    key={c.key}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-xs font-medium text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-900"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                    <span className="whitespace-nowrap">{t(c.key)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-col gap-2">
            <LanguageSwitcher />
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}